import React, { useContext, useEffect, useState } from "react";
import { ESDataTable } from "../../controls/ESDataTable";
import {
  ConfigurationDetailInsert,
  getAllConfigurationDetail
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";

import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { UserContext } from "../../routes/routes";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import {
  checkIfSuperAdmin,
  formatDateCommon,
  getCachedData,
  Option,
} from "../../utils/common";
import Footer from "../footer/Footer";
import Header from "../header/Header";
import Loader from "../loader/Loader";
import SubHeader from "../subheader/Subheader";

interface ConfigurationDetail {
  ConfigurationDetailId: number;
  ConfigurationId: number;
  OrganizationId: number;
  Value: string;
  IsActive: boolean;
  CreateDate: string;
  UpdateDate: string;
  CreateUserId: number | null;
  UpdateUserId: number | null;
  InternalKey: string | null;
  ConcurrencyKey: string | null;
  ConfigurationDataType: string;
  ConfigurationName: string;
}

const Settings: React.FC<{}> = () => {
  const [selectedRows, setSelectedRows] = useState<ConfigurationDetail[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [organizationDropDownChange, setOrganizationDropDownChange] =
    useState(false);
  const [defaultOraganizationDropDown, setDefaultOraganizationDropDown] =
    useState<any>(null);
  const [organizationId, setOrganizationId] = useState<number | 0>(0);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { appContextdata } = useContext<any>(UserContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const methods = useForm();

  const [selectedOrganization, setSelectedOrganization] = useState<number>(0);
  const [configurationsDetail, setConfigurationsDetail] = useState<
    ConfigurationDetail[]
  >([]);

  const fetchConfigurationDetails = async () => {
    try {
      setLoading(true);
      const response: any = await axiosPost(getAllConfigurationDetail, {
        OrganizationId: organizationId,
      });
      if (response?.data?.ExtraData) {
        setConfigurationsDetail(response?.data?.ExtraData);

        const activeRows = response?.data?.ExtraData?.filter(
          (item: ConfigurationDetail) => item?.IsActive
        );

        setSelectedRows(activeRows);
      }
    } catch (error) {
      showToast("Failed to load configuration details.", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurationDetails();
  }, [organizationId]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);

      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData("organizations")) as Option[];
        setOrganizations(options);
        const defaultOption: any = options?.find(
          (option) => option?.value === appContextdata?.Organization
        );
        handleOrganizationChange(defaultOption);
        setDefaultOraganizationDropDown(defaultOption);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const handleRowCheckboxToggle = async (row: ConfigurationDetail) => {
    const isSelected = selectedRows.some(
      (selectedRow) => selectedRow?.ConfigurationId === row?.ConfigurationId
    );

    if (isSelected) {
      setSelectedRows(
        selectedRows.filter(
          (selectedRow) => selectedRow?.ConfigurationId !== row?.ConfigurationId
        )
      );

      try {
        setLoading(true);
        const response: any = await axiosPost(ConfigurationDetailInsert, {
          ConfigurationId: row?.ConfigurationId,
          Value: row.Value,
          IsActive: false,
          CreateDate: new Date(),
          UpdateDate: new Date(),
        });
        if (response?.status === 200) {
          const message = "Configuration DeActivated Successfully";
          showToast(message, ToastTypeCode?.SUCCESS);
        }
      } catch (error) {
        setError("Error fetching data");
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedRows([...selectedRows, row]);

      try {
        setLoading(true);
        const response: any = await axiosPost(ConfigurationDetailInsert, {
          ConfigurationId: row?.ConfigurationId,
          Value: row?.Value,
          IsActive: true,
        });
        if (response?.status === 200) {
          const message = "Configuration activated successfully.";
          showToast(message, ToastTypeCode?.SUCCESS);
        }
      } catch (error) {
        setError("Failed to activate configuration.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOrganizationChange = (selectedOption: Option | null) => {
    setDefaultOraganizationDropDown(null);
    setOrganizationId(selectedOption?.value ?? 0);
    setCurrentPage(1);
    setOrganizationDropDownChange(true);
    setSelectedOrganization(organizationId);
  };

  const columns = [
    {
      name: "Select",
      selector: (row: ConfigurationDetail) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={selectedRows.some(
            (selectedRow) => selectedRow.ConfigurationId === row.ConfigurationId
          )}
          onChange={() => handleRowCheckboxToggle(row)}
          data-tour="enable-captcha"
        />
      ),
      grow: 1,
      width: "60px",
    },
    {
      name: "Configuration Name",
      selector: (row: ConfigurationDetail) => row.ConfigurationName,
      sortable: true,
      sortField: "ConfigurationName",
      grow: 2,
    },
    {
      name: "ConfigurationDataType",
      selector: (row: ConfigurationDetail) => row.ConfigurationDataType,
      sortable: true,
      sortField: "ConfigurationDataType",
      grow: 4,
    },
    {
      name: "Last Modified",
      selector: (row: ConfigurationDetail) => formatDateCommon(row.CreateDate),
      sortable: true,
      sortField: "CreateDate",
      grow: 1,
    },
  ];

  return (
    <>
      <div className="wrap-userlocation">
        <Header />
        <SubHeader />
        <div className="usertemplate-blk">
          <Container fluid>
            <Card>
              <CardHeader>
                <Row>
                  <Col lg={6}>
                    <h2 className="page-title">Settings</h2>
                  </Col>
                  <Col lg={6}>
                    <div className="search-usertemplate">
                      {isSuperAdmin && (
                        <FormProvider {...methods}>
                          {organizations && organizations?.length >= 1 && (
                            <ESSearchDropdown
                              name="OrganizationId"
                              placeholder="Select Organization"
                              options={organizations}
                              onChange={handleOrganizationChange}
                              defaultValue={defaultOraganizationDropDown}
                            />
                          )}
                        </FormProvider>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {error && <p>{error}</p>}
                <div className="table-responsive">
                  <ESDataTable
                    columns={columns}
                    data={configurationsDetail}
                    paginationTotalRows={totalRecords}
                    onChangePage={() => {}}
                    onSort={() => {}}
                    onChangeRowsPerPage={() => {}}
                  />
                </div>
              </CardBody>
            </Card>
          </Container>
        </div>
        <Footer />
        {loading ? <Loader /> : null}
      </div>
    </>
  );
};

export default Settings;
